<template>
	<section>
		<template v-if="!readonly">
			<v-divider/>
			<div class="pa-6 text-center grey lighten-5">
				<AsiBtn :icon="icons.avatar" @click="uploadDialog.open()" color="secondary">
					{{ $t('ui.terms.uploadGalleryImages') }}
				</AsiBtn>
			</div>
		</template>

		<template v-if="category !== null">
			<v-divider/>
			<AsiContentContainer v-if="category.galleryAttachments.length > 0" :columns="2" :columns-lg="3" :columns-xl="6" with-row-gap>
				<div v-for="image in imagesSorted" :key="image.id">
					<AsiCard unwrapped no-bottom-margin class="overflow-hidden">
						<v-card-text class="card-content pa-0">
							<div class="pa-3">
								<v-img :src="imageUrl(image)" aspect-ratio="1" contain/>
							</div>
							<v-divider/>

							<div class="avatar-overlay text-center px-6 py-3">
								<v-tooltip v-if="isAvatar(image)" top>
									<template v-slot:activator="{on, attrs}">
										<span v-on="on" v-bind="attrs">
											<v-avatar
												size="32"
												color="rgba(0,0,0,0.6)"
												class="elevation-2"
											>
												<v-icon color="white" size="18">
											        {{ icons.avatar }}
												</v-icon>
											</v-avatar>
										</span>
									</template>
									{{ $t('category.terms.avatar') }}
								</v-tooltip>
								<v-tooltip v-if="isBannerImage(image)" top>
									<template v-slot:activator="{on, attrs}">
										<span v-on="on" v-bind="attrs">
											<v-avatar
												size="32"
												color="rgba(0,0,0,0.6)"
												class="elevation-2"
											>
											    <v-icon size="18" color="white">
													{{ icons.bannerImage }}
												</v-icon>
											</v-avatar>
										</span>
									</template>
									{{ $t('category.terms.banner') }}
								</v-tooltip>
								<v-tooltip v-if="isCarouselImage(image)" top>
									<template v-slot:activator="{on, attrs}">
										<span v-on="on" v-bind="attrs">
											<v-avatar
												size="32"
												color="rgba(0,0,0,0.6)"
												class="elevation-2"
											>
											    <v-icon size="18" color="white">
													{{ icons.panorama }}
												</v-icon>
											</v-avatar>
										</span>
									</template>
									{{ $t('category.terms.carousel') }}
								</v-tooltip>
								<v-tooltip v-if="image.needsCropping" top>
									<template v-slot:activator="{on, attrs}">
										<span v-on="on" v-bind="attrs">
											<v-avatar
												size="32"
												color="primary"
												class="elevation-2"
											>
											    <v-icon size="18" color="white">
													{{ icons.frameAssembly }}
												</v-icon>
											</v-avatar>
										</span>
									</template>
									{{ $t('attachment.needsCropping') }}
								</v-tooltip>
							</div>
						</v-card-text>

						<template v-slot:actions>
							<AsiBtn :icon="icons.preview"
							        @click="previewAttachment = image; previewDialog.open()"/>
							<AsiBtn @click="downloadAttachment(image)" :icon="icons.download"/>
							<AsiBtn v-if="!readonly" @click="performDelete(image)"
							        :icon="icons.delete"/>
						</template>

						<template v-slot:top-content>
							<div class="d-flex flex-row flex-wrap">
								<v-menu bottom offset-y left>
									<template v-slot:activator="{on, attrs}">
										<span v-on="on" v-bind="attrs" class="ml-auto">
											<AsiBtn :icon="icons.menu"/>
										</span>
									</template>

									<AsiCard unwrapped no-bottom-margin>
										<v-list class="pa-0">
											<AsiMenuItem v-if="!readonly"
											             :icon="icons.noFavorite"
											             :label="category.avatarAttachmentId === image.id ? $t('category.terms.removeAvatar') : $t('category.terms.setAvatar')"
											             @click="performSetAvatar(image)"/>
											<v-divider />
											<AsiMenuItem v-if="!readonly"
											             :icon="icons.frameAssembly"
													     :disabled="image.needsCropping"
											             :label="$t('category.terms.setNeedsCropping')"
											             @click="performSetNeedsCropping(image)"/>
											<v-divider/>
											<AsiMenuItem v-if="!readonly"
											             :icon="icons.bannerImage"
											             :label="category.bannerAttachmentId === image.id ?  $t('category.terms.removeBanner') : $t('category.terms.setBanner')"
											             @click="performSetBanner(image)"/>
											<v-divider/>
											<AsiMenuItem v-if="!readonly"
											             :icon="icons.panorama"
											             :label="category.carouselAttachmentId === image.id ?  $t('category.terms.removeCarousel') : $t('category.terms.setCarousel')"
											             @click="performSetCarousel(image)"/>
										</v-list>
									</AsiCard>
								</v-menu>
							</div>
						</template>
					</AsiCard>
				</div>
			</AsiContentContainer>
			<div v-else class="pa-6 text-center grey--text">
				{{ $t('ui.terms.noImages') }}
			</div>
		</template>

		<AsiConfirmDialog ref="confirm"/>
		<ImageGalleryUploadDialog v-if="!readonly && uploadDialog.isLoaded" :open="uploadDialog.isOpen"
		                          :model="category" :service="$categoryServiceAdmin"
		                          @cancel="uploadDialogCancelled"/>
		<AttachmentPreviewDialog v-if="previewAttachment !== null && previewDialog.isLoaded" :open="previewDialog.isOpen"
		                         :attachment="previewAttachment" @cancel="previewDialog.close()"/>
	</section>
</template>

<script lang="ts">
	import Vue from 'vue';
	import {Component, Emit, Prop} from "vue-property-decorator";
	import {ICategoryAdmin} from "@/models/category/CategoryAdminModels";
	import Icon from "@/plugins/icons";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import {AttachmentMediaSize} from "@/helpers/constants";
	import AsiContentContainer from "@/components/common/AsiContentContainer.vue";
	import {IAttachment} from "@/models/attachment/AttachmentModels";
	import AttachmentHelper from "@/models/attachment/AttachmentHelper";
	import ImageGalleryUploadDialog from "@/components/common/ImageGalleryUploadDialog.vue";
	import DialogHandler from "@/components/common/DialogHandler";
	import AsiCard from "@/components/common/AsiCard.vue";
	import AttachmentPreviewDialog from "@/components/attachment/AttachmentPreviewDialog.vue";
	import AsiChip from "@/components/common/AsiChip.vue";
	import Snackbar from "@/helpers/Snackbar";
	import AsiConfirmDialog from "@/components/common/AsiConfirmDialog.vue";
	import AsiConfirmDialogDefinition from "@/helpers/AsiConfirmDialogDefinition";
	import AsiMenuItem from "@/components/common/AsiMenuItem.vue";

	@Component({
		components: {
			AsiMenuItem,
			AsiConfirmDialog,
			AsiChip, AttachmentPreviewDialog, AsiCard, ImageGalleryUploadDialog, AsiContentContainer, AsiBtn
		}
	})
	export default class CategoryTabImages extends Vue {

		@Prop({required: true})
		public category!: ICategoryAdmin | null;

		@Prop({type: Boolean, default: false})
		public readonly!: boolean;

		private icons = Icon;
		private loading: boolean = false;
		private uploadDialog: DialogHandler = new DialogHandler();
		private previewDialog: DialogHandler = new DialogHandler(() => this.previewAttachment = null);
		private previewAttachment: IAttachment | null = null;

		private get imagesSorted(): IAttachment[] {
			return this.category === null
				? []
				: this.category.galleryAttachments.sort((a: IAttachment, b: IAttachment) => {
					if (this.isAvatar(a)) return -1;
					if (this.isAvatar(b)) return 1;
					return a.filename.localeCompare(b.filename);
				});
		}

		@Emit('change')
		public change(): void {
			return;
		}

		// noinspection JSMethodCanBeStatic
		private imageUrl(image: IAttachment): string | null {
			return AttachmentHelper.getImageUrl(image, AttachmentMediaSize.m);
		}

		private isAvatar(image: IAttachment): boolean {
			return this.category?.avatarAttachmentId === image.id;
		}

		private isBannerImage(image: IAttachment): boolean {
			return this.category?.bannerAttachmentId === image.id;
		}

		private isCarouselImage(image: IAttachment): boolean {
			return this.category?.carouselAttachmentId === image.id;
		}

		private uploadDialogCancelled(numSuccessful: number): void {
			this.uploadDialog.close();
			if (numSuccessful > 0) this.change();
		}

		private performSetAvatar(image: IAttachment): void {
			if (this.category === null) return;

			this.loading = true;
			this.$categoryServiceAdmin.setGalleryImageAvatar(this.category.id, this.isAvatar(image) ? null : image.id)
				.then(() => this.change())
				.catch(() => Snackbar.updateError())
				.finally(() => this.loading = false);
		}

		private performSetNeedsCropping(image: IAttachment): void {
			if (this.category === null) return;

			this.loading = true;
			this.$categoryServiceAdmin.patchGalleryImageNeedsCropping(this.category.id, image.id, true)
				.then(() => this.change())
				.catch(() => Snackbar.updateError())
				.finally(() => this.loading = false);
		}

		private performSetBanner(image: IAttachment): void {
			if (this.category === null) return;

			this.loading = true;
			this.$categoryServiceAdmin.setGalleryImageBanner(this.category.id, this.isBannerImage(image) ? null : image.id)
				.then(() => this.change())
				.catch(() => Snackbar.updateError())
				.finally(() => this.loading = false);
		}

		private performDelete(image: IAttachment): void {
			if (this.category === null) return;

			const confirm = this.$refs.confirm as unknown as AsiConfirmDialogDefinition;
			confirm.openDialog().then((res: boolean) => {
				if (!res || this.category === null) return;

				this.loading = true;
				this.$categoryServiceAdmin.deleteGalleryImage(this.category.id, image.id)
					.then(() => this.change())
					.catch(() => Snackbar.deleteError())
					.finally(() => this.loading = false);
			});
		}

		private performSetCarousel(image: IAttachment): void {
			if (this.category === null) return;

			this.loading = true;
			this.$categoryServiceAdmin.setGalleryImageCarousel(this.category.id, this.isCarouselImage(image) ? null : image.id)
				.then(() => this.change())
				.catch(() => Snackbar.updateError())
				.finally(() => this.loading = false);
		}

		private downloadAttachment(attachment: IAttachment): void {
			AttachmentHelper.download(attachment);
		}

	}
</script>

<style lang="scss" scoped>
	@import '~vuetify/src/styles/styles';

	.card-content {
		position: relative;

		.avatar-overlay {
			position: absolute;
			bottom: -1.27rem;
			width: 100%;
			left: 0;
			display: flex;
			flex-direction: row;
			justify-content: center;
			gap: 0.5rem;
		}
	}
</style>
